import React, { createContext, useContext, useReducer, useCallback } from 'react'
import { AuthState } from '../types/auth'

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------
const defaultAuthState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  pendingLoginCode: null,
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
type AuthAction =
  | { type: 'SET'; payload: AuthState }
  | { type: 'FUNCTIONAL_UPDATE'; updater: (prev: AuthState) => AuthState }

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET':
      return action.payload
    case 'FUNCTIONAL_UPDATE':
      return action.updater(state)
  }
}

// ---------------------------------------------------------------------------
// Setter type — accepts either a new value or an updater function.
// ---------------------------------------------------------------------------
export type SetAuthState = (valOrUpdater: AuthState | ((prev: AuthState) => AuthState)) => void

// ---------------------------------------------------------------------------
// Contexts
// ---------------------------------------------------------------------------
const AuthStateContext = createContext<AuthState>(defaultAuthState)
const AuthDispatchContext = createContext<SetAuthState>(() => {})

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const AuthStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, defaultAuthState)

  const setAuth: SetAuthState = useCallback(valOrUpdater => {
    if (typeof valOrUpdater === 'function') {
      dispatch({ type: 'FUNCTIONAL_UPDATE', updater: valOrUpdater })
    } else {
      dispatch({ type: 'SET', payload: valOrUpdater })
    }
  }, [])

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={setAuth}>{children}</AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Read auth state */
export const useAuthState = (): AuthState => useContext(AuthStateContext)

/** Get auth state setter */
export const useSetAuthState = (): SetAuthState => useContext(AuthDispatchContext)

/**
 * Read + write auth state.
 * Returns [state, setter] tuple.
 */
export const useAuthStateValue = (): [AuthState, SetAuthState] => {
  return [useAuthState(), useSetAuthState()]
}
